/**
 * `<SavedRulingLibrary>` — the GM's saved-ruling **library manager**, on the EDIT inspector.
 *
 * **This is a manager, NOT a composer, and that boundary is deliberate.** It lists what the GM
 * has saved, renames a row, and deletes one. It cannot author a ruling from nothing — the only
 * creation gesture is `KEEP` on the PLAY palette's arm, so a GM composes by firing a ruling and
 * saving it. Don't grow a spec editor here without the developer asking for one.
 *
 * **Why EDIT and not PLAY.** The PLAY palette is on a five-second attention budget — a GM
 * mid-encounter fires a ruling in two clicks and must not be shown rename/delete controls that
 * are irrelevant to that gesture and could be misclicked. Management belongs where authoring
 * belongs, on the EDIT desktop.
 *
 * **The code-shipped presets are structurally unreachable from here**, which is the point: this
 * component only ever renders `session.savedRulings`, so a GM cannot delete the zero-setup floor
 * their palette depends on, by misclick or otherwise.
 *
 * **Deleting a row never disturbs a live status.** Firing a saved ruling copies the spec inline
 * onto the `StatusInstance` — nothing on the board points back at a library entry — so a ruling
 * granted from a row keeps meaning exactly what it meant when it was granted, mid-fight, after the
 * row is gone. The delete control says so rather than leaving a GM to wonder.
 */
import { useState } from "react";
import type { SavedRuling } from "@lancer-vtt/domain";
import { useGameSessionContext } from "../game/GameSessionProvider";

/** A one-line, scannable read of what a saved row actually does — mirrors the CLI's `list-rulings`. */
export function savedRulingSummary(r: SavedRuling): string {
  const bits: string[] = [];
  if (r.ruling.accuracy !== undefined && r.ruling.accuracy !== 0) {
    bits.push(`${r.ruling.accuracy > 0 ? "+" : ""}${r.ruling.accuracy} ${r.ruling.accuracy > 0 ? "accuracy" : "difficulty"}`);
  }
  const dice = r.ruling.extraDamage?.dice;
  if (dice !== undefined) bits.push(`+${dice.count}d${dice.sides} damage`);
  if (r.ruling.extraDamage?.amount !== undefined) bits.push(`+${r.ruling.extraDamage.amount} damage`);
  if (r.ruling.verb !== undefined) bits.push(`${r.ruling.verb} only`);
  if (r.ruling.consumeOnAttack === true) bits.push("next attack");
  if (r.expiry?.kind === "turn") bits.push("until end of turn");
  // A ruling with no mechanics is an ADVISORY, and saying so plainly is the honest read — the
  // palette's own advisory presets make the same promise-nothing claim out loud, because a GM
  // who thinks a row takes a die off the damage will build a turn around it.
  return bits.length > 0 ? bits.join(" · ") : "advisory — carries the reason, no engine math";
}

export function SavedRulingLibrary({ disabled }: { disabled: boolean }) {
  const session = useGameSessionContext();
  const [renaming, setRenaming] = useState<{ id: string; label: string } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  function commitRename() {
    if (renaming === null) return;
    const entry = session.savedRulings.find((r) => r.id === renaming.id);
    const label = renaming.label.trim();
    // An empty rename is a no-op rather than an error: the reducer would reject it anyway
    // (`save_ruling` requires a non-empty label), and mirroring that as an affordance beats
    // sending a command doomed to be a silent server-side no-op.
    if (entry !== undefined && label !== "") {
      // A rename IS a save onto the same id — no separate edit command exists, so the whole
      // entry rides along with only the label changed.
      // Link down: keep the rename open so the typed label is not lost.
      if (!session.saveRuling({ ...entry, label })) return;
    }
    setRenaming(null);
  }

  return (
    <div className="npc-ctl-edit-field npc-ctl-ruling-library">
      <span className="npc-ctl-label">Saved GM rulings</span>
      {session.savedRulings.length === 0 ? (
        // The empty state teaches the creation gesture, because there is no composer here to
        // discover — a GM who reads "none saved" and nothing else would reasonably conclude the
        // feature is broken rather than that authoring lives on the other desktop.
        <p className="npc-ctl-ruling-library-empty">
          None saved. Fire a ruling from the PLAY inspector and click KEEP to add it here.
        </p>
      ) : (
        <ul className="npc-ctl-ruling-library-list">
          {session.savedRulings.map((r) => (
            <li key={r.id} className="npc-ctl-ruling-library-row">
              {renaming?.id === r.id ? (
                <>
                  <input
                    className="npc-ctl-ruling-savename"
                    value={renaming.label}
                    autoFocus
                    aria-label={`rename ${r.label}`}
                    onChange={(e) => setRenaming({ id: r.id, label: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename();
                      if (e.key === "Escape") setRenaming(null);
                    }}
                  />
                  <button type="button" className="npc-ctl-ruling-saveok" onClick={commitRename}>
                    OK
                  </button>
                </>
              ) : (
                <>
                  <div className="npc-ctl-ruling-library-main">
                    <span className="npc-ctl-ruling-library-label">{r.label}</span>
                    <span className="npc-ctl-ruling-library-spec">{savedRulingSummary(r)}</span>
                  </div>
                  <button
                    type="button"
                    className="npc-ctl-ruling-library-btn"
                    disabled={disabled}
                    aria-label={`rename ${r.label}`}
                    onClick={() => {
                      setRenaming({ id: r.id, label: r.label });
                      setConfirmingDelete(null);
                    }}
                  >
                    RENAME
                  </button>
                  {confirmingDelete === r.id ? (
                    <button
                      type="button"
                      className="npc-ctl-ruling-library-btn is-danger"
                      disabled={disabled}
                      aria-label={`confirm delete ${r.label}`}
                      // Deleting is undoable at the Game tier (`lvtt game undo` / the GM's undo
                      // stack), so the confirm is a misclick guard, not a point of no return.
                      title="Delete this saved ruling — live statuses granted from it are unaffected"
                      onClick={() => {
                        if (session.deleteSavedRuling(r.id)) setConfirmingDelete(null);
                      }}
                    >
                      SURE?
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="npc-ctl-ruling-library-btn"
                      disabled={disabled}
                      aria-label={`delete ${r.label}`}
                      onClick={() => setConfirmingDelete(r.id)}
                    >
                      DELETE
                    </button>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
